import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AlertTriangle, Loader2, Plus } from "lucide-react";

import { MetadataSelect } from "@/components/itsm-config/metadata-select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useDeskCustomFields } from "@/hooks/use-desk-migration";
import { useJsmCustomFields } from "@/hooks/use-itsm-metadata";
import type { JsmCustomField } from "@/hooks/use-itsm-metadata";
import type {
  CustomFieldImportMethod,
  CustomFieldMapping,
} from "@/lib/types/itsm-import";

import {
  getMappingErrors,
  isDefaultViewField,
  JSM_FIELD_ID_PATTERN,
} from "./custom-field-mappings";

const METHOD_OPTIONS: { value: CustomFieldImportMethod; label: string }[] = [
  { value: "description", label: "Append to description" },
  { value: "harmony_custom_field", label: "Map to Harmony custom field" },
];

// The project key is free text, so wait for typing to settle before sampling.
const PROJECT_KEY_DEBOUNCE_MS = 400;

interface FieldRow {
  fieldId: string;
  name: string;
  /** Absent for fields added by ID or carried over from a cloned job. */
  discovered?: JsmCustomField;
}

interface ManualField {
  fieldId: string;
  name: string;
}

interface Props {
  tenantId: string;
  credentialId: string;
  projectKey: string;
  targetDeskId: string;
  /** null imports every field on the Jira issue view into the description. */
  value: CustomFieldMapping[] | null;
  onChange: (value: CustomFieldMapping[] | null) => void;
  showRequiredErrors: boolean;
}

export function CustomFieldsSelector({
  tenantId,
  credentialId,
  projectKey,
  targetDeskId,
  value,
  onChange,
  showRequiredErrors,
}: Props) {
  const enabled = value !== null;
  const mappings = value ?? [];

  const [debouncedKey, setDebouncedKey] = useState(projectKey);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedKey(projectKey);
    }, PROJECT_KEY_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [projectKey]);

  const discovery = useJsmCustomFields(
    enabled ? tenantId : undefined,
    credentialId,
    debouncedKey,
  );
  const deskFields = useDeskCustomFields(tenantId, enabled ? targetDeskId : "");

  const [filter, setFilter] = useState("");
  // The checkbox choice belongs to the project it was made for, like `manual`
  // and `kept` below: an untick carried into the next project would hide that
  // project's whole list. null means untouched, so the default below applies.
  const [extra, setExtra] = useState<{
    projectKey: string;
    choice: boolean | null;
  }>({ projectKey, choice: null });
  const extraChoice = extra.projectKey === projectKey ? extra.choice : null;
  const [manualId, setManualId] = useState("");
  const [manualName, setManualName] = useState("");
  // Manually added fields belong to the project they were added for.
  const [manual, setManual] = useState<{
    projectKey: string;
    fields: ManualField[];
  }>({ projectKey, fields: [] });
  const manualFields = manual.projectKey === projectKey ? manual.fields : [];

  const harmonyOptions = useMemo(
    () =>
      [...new Set((deskFields.data ?? []).map((f) => f.name))].map((name) => ({
        value: name,
        label: name,
      })),
    [deskFields.data],
  );
  const harmonyNames = deskFields.data
    ? new Set(harmonyOptions.map((o) => o.value))
    : undefined;

  const discovered = discovery.data?.fields ?? [];
  const sampledIssues = discovery.data?.sampled_issues;
  const lookupsIncomplete = discovery.data?.lookups_incomplete ?? false;
  // Jira returned no issue types for the project, so the credential cannot
  // read its create screens and every field came from sampling alone.
  const createScreensUnavailable =
    discovery.data?.create_screens_unavailable ?? false;
  // Request-form and issue-view fields are the useful default: a requester
  // fills the first in, and agents see the second on the ticket. The rest -
  // automation/global fields on a create screen, fields tucked into issue-view
  // tabs, plus the sampled-only tail - is large and mostly noise, so it is
  // opt-in. Jira Software has neither, and with nothing to anchor on the
  // default would hide everything; there it falls back to create-screen
  // fields.
  const hasDefaultFields = discovered.some(isDefaultViewField);
  const showExtra =
    extraChoice ?? (createScreensUnavailable || !hasDefaultFields);
  const knownIds = new Set(discovered.map((f) => f.field_id));
  const extraFields = new Map<string, string>();
  for (const f of manualFields) extraFields.set(f.fieldId, f.name);
  for (const m of mappings) {
    if (!extraFields.has(m.source_field_id)) {
      extraFields.set(
        m.source_field_id,
        m.source_field_name ?? m.source_field_id,
      );
    }
  }
  const rows: FieldRow[] = [
    ...[...extraFields]
      .filter(([fieldId]) => !knownIds.has(fieldId))
      .map(([fieldId, name]) => ({ fieldId, name })),
    ...discovered.map((f) => ({
      fieldId: f.field_id,
      name: f.name,
      discovered: f,
    })),
  ];

  const byId = new Map(mappings.map((m) => [m.source_field_id, m]));

  /** Hidden behind the toggle by default: a field neither on a request form
   *  nor on an agent issue view - automation and global fields, issue-view
   *  tab contents, plus the sampled-only tail. When the project has neither,
   *  there is nothing to rank against, so it falls back to the create-screen
   *  split. A manually added row has no discovery and is always shown. */
  const isExtra = (row: FieldRow) => {
    const field = row.discovered;
    if (!field) return false;
    if (hasDefaultFields) return !isDefaultViewField(field);
    return field.on_create_screen === false;
  };
  const extraCount = rows.filter(isExtra).length;

  const needle = filter.trim().toLowerCase();
  const matchesNeedle = (r: FieldRow) =>
    !needle ||
    r.name.toLowerCase().includes(needle) ||
    r.fieldId.toLowerCase().includes(needle);

  // Bumped on every filter or toggle change, so rows kept listed for one view
  // are not resurrected by navigating back to that view.
  const [viewEpoch, setViewEpoch] = useState(0);
  const bumpView = () => {
    setViewEpoch((n) => n + 1);
  };
  // A row the user just excluded would otherwise drop out of the list under
  // the cursor, so it stays listed until the view or the project changes.
  const [kept, setKept] = useState<{
    epoch: number;
    projectKey: string;
    ids: string[];
  }>({ epoch: 0, projectKey, ids: [] });
  const keptIds =
    kept.epoch === viewEpoch && kept.projectKey === projectKey ? kept.ids : [];
  const keepListed = (fieldId: string) => {
    if (!keptIds.includes(fieldId)) {
      setKept({ epoch: viewEpoch, projectKey, ids: [...keptIds, fieldId] });
    }
  };

  /** The rows the current filter and toggle actually select for. */
  const naturallyListed = (r: FieldRow) =>
    matchesNeedle(r) && (showExtra || !isExtra(r));
  // An included field is always listed, so neither the filter nor the
  // toggle can hide a selection the user has already made.
  const visibleRows = rows.filter(
    (r) =>
      byId.has(r.fieldId) || keptIds.includes(r.fieldId) || naturallyListed(r),
  );
  // Bulk select must not re-include a row that is only on screen because it
  // was selected or was just deselected.
  const selectableRows = visibleRows.filter(naturallyListed);
  const visibleIds = new Set(visibleRows.map((r) => r.fieldId));
  // Fields the search would have matched, hidden only by the toggle.
  const hiddenMatches = rows.filter(
    (r) => matchesNeedle(r) && !visibleIds.has(r.fieldId),
  ).length;
  // The create-screen split only exists once the backend reports the flag.
  const hasCreateScreenData = discovered.some(
    (f) => f.on_create_screen !== undefined,
  );
  const errors = getMappingErrors(mappings, harmonyNames);

  const matchHarmonyField = (name: string) =>
    harmonyOptions.find((o) => o.value.toLowerCase() === name.toLowerCase())
      ?.value ?? null;

  const defaultMapping = (row: FieldRow): CustomFieldMapping => {
    const match = matchHarmonyField(row.name);
    return {
      source_field_id: row.fieldId,
      source_field_name: row.name,
      method: match ? "harmony_custom_field" : "description",
      harmony_field_name: match,
    };
  };

  const include = (toAdd: FieldRow[]) => {
    const added = toAdd.filter((r) => !byId.has(r.fieldId));
    if (added.length > 0) onChange([...mappings, ...added.map(defaultMapping)]);
  };
  const exclude = (toRemove: FieldRow[]) => {
    const ids = new Set(toRemove.map((r) => r.fieldId));
    onChange(mappings.filter((m) => !ids.has(m.source_field_id)));
  };
  const update = (fieldId: string, patch: Partial<CustomFieldMapping>) => {
    onChange(
      mappings.map((m) =>
        m.source_field_id === fieldId ? { ...m, ...patch } : m,
      ),
    );
  };
  const setMethod = (row: FieldRow, method: CustomFieldImportMethod) => {
    update(
      row.fieldId,
      method === "description"
        ? { method, harmony_field_name: null }
        : {
            method,
            harmony_field_name:
              byId.get(row.fieldId)?.harmony_field_name ??
              matchHarmonyField(row.name),
          },
    );
  };

  const manualIdTrimmed = manualId.trim();
  const manualIdValid = JSM_FIELD_ID_PATTERN.test(manualIdTrimmed);
  const addManual = () => {
    if (!manualIdValid) return;
    const row = rows.find((r) => r.fieldId === manualIdTrimmed) ?? {
      fieldId: manualIdTrimmed,
      name: manualName.trim() || manualIdTrimmed,
    };
    if (
      !row.discovered &&
      !manualFields.some((f) => f.fieldId === row.fieldId)
    ) {
      setManual({
        projectKey,
        fields: [...manualFields, { fieldId: row.fieldId, name: row.name }],
      });
    }
    include([row]);
    setManualId("");
    setManualName("");
  };

  const isDiscovering = debouncedKey !== projectKey || discovery.isPending;
  const notFound =
    axios.isAxiosError(discovery.error) &&
    discovery.error.response?.status === 404;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
        <div className="space-y-1">
          <Label htmlFor="custom-fields-toggle">
            Pick fields or map them to Harmony custom fields
          </Label>
          <p className="text-muted-foreground text-sm">
            {enabled
              ? "Only the fields included below are imported. Every other custom field is dropped."
              : "Every custom field agents see on the Jira issue view — the portal header, main column and sidebar — is added to the ticket description, in the order Jira shows it. Turn this on only to limit the fields or set Harmony custom fields."}
          </p>
        </div>
        <Switch
          id="custom-fields-toggle"
          checked={enabled}
          onCheckedChange={(checked) => {
            onChange(checked ? [] : null);
          }}
        />
      </div>

      {enabled && (
        <div className="space-y-3">
          {/* Held back while discovering: the response is what says which of
              the two descriptions is the true one. */}
          {!isDiscovering && (
            <p className="text-muted-foreground text-xs">
              Jira Form (ProForma) answers are always added to the description.{" "}
              {hasCreateScreenData && !createScreensUnavailable ? (
                hasDefaultFields ? (
                  <>
                    Fields on {projectKey}'s request forms — the ones a
                    requester fills in — come first, then the fields agents see
                    on each request type's issue view. Other agent and
                    automation fields are behind the checkbox below; anything
                    the project never exposes can be added by ID.
                  </>
                ) : (
                  <>
                    Fields come from the create screens of {projectKey}'s issue
                    types, then by how often they carry a value in the{" "}
                    {sampledIssues === undefined ? "" : `${sampledIssues} `}most
                    recent issues. Fields seen only in ticket data are behind
                    the checkbox below; anything the project never exposes can
                    be added by ID.
                  </>
                )
              ) : (
                <>
                  Fields are discovered from the{" "}
                  {sampledIssues === undefined ? "" : `${sampledIssues} `}most
                  recent issues in {projectKey}, so rarely used fields may be
                  missing. Add those by ID below.
                </>
              )}
            </p>
          )}

          {/* Mounted whether or not there is a warning, so the text arrives as
              a mutation inside a live region that already exists — a region
              inserted together with its text is announced unreliably. */}
          <div role="status">
            {!isDiscovering &&
              !discovery.isError &&
              (createScreensUnavailable || lookupsIncomplete) && (
                <div className="flex items-start gap-3 rounded-lg border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-800 dark:bg-yellow-900/20">
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-yellow-600 dark:text-yellow-400" />
                  <p className="flex-1 text-sm text-yellow-800 dark:text-yellow-200">
                    {createScreensUnavailable ? (
                      <>
                        Jira didn't return any issue types for {projectKey}, so
                        its create screens and request-form details couldn't be
                        read — usually this means the credential lacks Create
                        Issue permission there. Every discovered field is shown
                        below instead; grant that permission for the full list.
                      </>
                    ) : (
                      <>
                        Some Jira lookups didn't finish, so request-form and
                        issue-view badges may be incomplete and some fields may
                        be missing. Try again, or add fields by ID.
                      </>
                    )}
                  </p>
                  {/* Retrying only helps the transient case: an empty issue
                      type list reflects the credential's own permissions, so it
                      answers the same on a retry, and each discovery costs
                      ~100+ Jira calls of the tenant's quota. Reloading the page
                      re-runs discovery if those permissions do change. */}
                  {!createScreensUnavailable && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={discovery.isFetching}
                      onClick={() => void discovery.refetch()}
                    >
                      {discovery.isFetching && (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      )}
                      Retry
                    </Button>
                  )}
                </div>
              )}
          </div>

          {isDiscovering ? (
            <div className="text-muted-foreground flex items-center gap-2 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              Discovering custom fields in {projectKey}...
            </div>
          ) : (
            <>
              {discovery.isError && (
                <div className="border-destructive/50 bg-destructive/5 text-destructive flex items-center justify-between gap-2 rounded-md border p-3 text-sm">
                  {notFound
                    ? `Project "${projectKey}" was not found for this credential.`
                    : "Failed to load the project's custom fields."}
                  {!notFound && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void discovery.refetch()}
                    >
                      Retry
                    </Button>
                  )}
                </div>
              )}

              {!discovery.isError && rows.length === 0 && (
                <p className="text-muted-foreground text-sm">
                  No custom fields were found for this project.
                </p>
              )}

              {rows.length > 0 && (
                <>
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Input
                        aria-label="Filter custom fields"
                        placeholder="Filter by name or field ID"
                        value={filter}
                        onChange={(e) => {
                          setFilter(e.target.value);
                          bumpView();
                        }}
                        className="h-8 max-w-xs"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          include(selectableRows);
                        }}
                      >
                        {needle
                          ? "Select matching"
                          : extraCount > 0 && !showExtra
                            ? "Select listed"
                            : "Select all"}
                      </Button>
                      {/* Every included field is listed, so this always
                          clears the whole selection — the label says so. */}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          exclude(visibleRows);
                        }}
                      >
                        {mappings.length > 0
                          ? `Clear all (${mappings.length})`
                          : "Clear"}
                      </Button>
                      <span className="text-muted-foreground ml-auto text-xs">
                        {mappings.length} included · {visibleRows.length} of{" "}
                        {rows.length} shown
                      </span>
                    </div>
                    {extraCount > 0 && (
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="show-extra-fields"
                          checked={showExtra}
                          onCheckedChange={(state) => {
                            setExtra({
                              projectKey,
                              choice: state === true,
                            });
                            bumpView();
                          }}
                        />
                        <Label
                          htmlFor="show-extra-fields"
                          className="text-muted-foreground text-xs font-normal"
                        >
                          {createScreensUnavailable
                            ? `Show all ${extraCount} discovered fields`
                            : hasDefaultFields
                              ? `Also show other agent & automation fields (${extraCount})`
                              : `Also show fields not on a create screen (${extraCount})`}
                        </Label>
                      </div>
                    )}
                  </div>

                  <div className="max-h-[28rem] overflow-auto rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10">
                            <span className="sr-only">Include</span>
                          </TableHead>
                          <TableHead>JSM field</TableHead>
                          <TableHead className="w-[200px]">Import as</TableHead>
                          <TableHead className="w-[210px]">
                            Harmony field
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {visibleRows.length === 0 && (
                          <TableRow>
                            <TableCell
                              colSpan={4}
                              className="text-muted-foreground text-center"
                            >
                              {!needle
                                ? createScreensUnavailable
                                  ? `Jira didn't reveal this project's create screens, so all ${rows.length} fields are hidden — tick the box above to show them.`
                                  : hasDefaultFields
                                    ? `None of the ${rows.length} custom fields are on a request form or issue view — tick the box above to show agent & automation fields.`
                                    : `None of the ${rows.length} custom fields are on a create screen — tick the box above to show them.`
                                : hiddenMatches > 0
                                  ? `${hiddenMatches} field${hiddenMatches === 1 ? "" : "s"} match${hiddenMatches === 1 ? "es" : ""} but ${hiddenMatches === 1 ? "is" : "are"} ${createScreensUnavailable ? "hidden" : hasDefaultFields ? "not on a request form or issue view" : "not on a create screen"} — tick the box above to show ${hiddenMatches === 1 ? "it" : "them"}.`
                                  : `No fields match "${filter.trim()}".`}
                            </TableCell>
                          </TableRow>
                        )}
                        {visibleRows.map((row) => {
                          const mapping = byId.get(row.fieldId);
                          const error = errors.get(row.fieldId);
                          const shownError =
                            error &&
                            (error.kind !== "missing" || showRequiredErrors)
                              ? error.message
                              : undefined;
                          return (
                            <TableRow
                              key={row.fieldId}
                              data-state={mapping ? "selected" : undefined}
                            >
                              <TableCell className="align-top">
                                <Checkbox
                                  aria-label={`Include ${row.name}`}
                                  checked={Boolean(mapping)}
                                  onCheckedChange={(state) => {
                                    if (state === true) include([row]);
                                    else {
                                      exclude([row]);
                                      keepListed(row.fieldId);
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell className="whitespace-normal align-top">
                                <FieldSummary
                                  row={row}
                                  sampledIssues={sampledIssues}
                                  createScreensUnavailable={
                                    createScreensUnavailable
                                  }
                                />
                              </TableCell>
                              <TableCell className="align-top">
                                {mapping ? (
                                  <Select
                                    value={mapping.method}
                                    onValueChange={(method) => {
                                      setMethod(
                                        row,
                                        method as CustomFieldImportMethod,
                                      );
                                    }}
                                  >
                                    <SelectTrigger className="h-8 w-full">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {METHOD_OPTIONS.map((o) => (
                                        <SelectItem
                                          key={o.value}
                                          value={o.value}
                                        >
                                          {o.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <span className="text-muted-foreground text-sm">
                                    Not imported
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="whitespace-normal align-top">
                                {mapping?.method === "harmony_custom_field" ? (
                                  <div className="space-y-1">
                                    <MetadataSelect
                                      options={harmonyOptions}
                                      value={mapping.harmony_field_name ?? ""}
                                      onChange={(name) => {
                                        update(row.fieldId, {
                                          harmony_field_name: name,
                                        });
                                      }}
                                      isLoading={deskFields.isLoading}
                                      allowCustom={false}
                                      disabled={!targetDeskId}
                                      placeholder={
                                        targetDeskId
                                          ? "Select Harmony field"
                                          : "Select a target desk first"
                                      }
                                    />
                                    {shownError && (
                                      <p className="text-destructive text-xs">
                                        {shownError}
                                      </p>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground">
                                    —
                                  </span>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}

              <div className="space-y-1">
                <Label className="text-xs">Add a field by ID</Label>
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    aria-label="Custom field ID"
                    aria-invalid={manualIdTrimmed !== "" && !manualIdValid}
                    placeholder="customfield_10050"
                    value={manualId}
                    onChange={(e) => {
                      setManualId(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addManual();
                      }
                    }}
                    className="h-8 w-48 font-mono"
                  />
                  <Input
                    aria-label="Display name"
                    placeholder="Display name (optional)"
                    value={manualName}
                    onChange={(e) => {
                      setManualName(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addManual();
                      }
                    }}
                    className="h-8 w-56"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!manualIdValid}
                    onClick={addManual}
                  >
                    <Plus className="h-4 w-4" />
                    Add field
                  </Button>
                </div>
                {manualIdTrimmed !== "" && !manualIdValid && (
                  <p className="text-destructive text-xs">
                    Field IDs look like customfield_10050
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function FieldSummary({
  row,
  sampledIssues,
  createScreensUnavailable,
}: {
  row: FieldRow;
  sampledIssues: number | undefined;
  createScreensUnavailable: boolean;
}) {
  const field = row.discovered;
  return (
    <div className="min-w-0 space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{row.name}</span>
        <span className="text-muted-foreground font-mono text-xs">
          {row.fieldId}
        </span>
        {!field ? (
          <Badge variant="warning">Not discovered</Badge>
        ) : field.in_request_form ? (
          <Badge variant="info">Request form</Badge>
        ) : field.in_issue_view ? (
          <Badge variant="info">Issue view</Badge>
        ) : (
          <Badge variant="outline" className="text-muted-foreground">
            Agent field
          </Badge>
        )}
        {field?.issue_view_section && (
          <Badge variant="outline" className="text-muted-foreground">
            Issue view · {field.issue_view_section}
          </Badge>
        )}
        {/* Every field carries false when the create screens could not be
            read, so the badge would assert an unknown on every row. */}
        {field?.on_create_screen === false && !createScreensUnavailable && (
          <Badge variant="outline" className="text-muted-foreground">
            Not on create screen
          </Badge>
        )}
      </div>
      {field && (
        <p className="text-muted-foreground text-xs">
          {field.issues_with_value === 0
            ? "no recent data"
            : `on ${field.issues_with_value} of ${sampledIssues ?? "?"} sampled issues`}
          {field.request_types.length > 0 &&
            ` · ${field.request_types.join(", ")}`}
        </p>
      )}
    </div>
  );
}
